'use client'

import { useEffect, useRef } from 'react'

import { Section, useThemeStore } from '@/components/findex-connect/store/ThemeStore'
import { FindexDifferenceBlockSb } from '@/components/findex-connect/types/component-types-sb'

import { BlurryLightsDaytime } from '../BlurryLights/BlurryLightsDaytime'
import { FindexFeatureCard } from '../FindexFeatureCard/FindexFeatureCard'
import { Carousel, CarouselContent, CarouselNext, CarouselPrevious } from '../ui/carousel'

type FindexDifferenceBlockProps = {
  sectionId: Section
  data: FindexDifferenceBlockSb
}

export const FindexDifferenceBlock: React.FC<FindexDifferenceBlockProps> = ({
  sectionId,
  data,
}) => {
  const sectionRef = useRef(null)

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0% 0% -50% 0%',
      threshold: 0.5,
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          useThemeStore.setState({
            activeSection: sectionId,
          })
        }
      })
    }, observerOptions)
    if (sectionRef?.current) {
      observer.observe(sectionRef.current)
    }
  }, [sectionRef, sectionId])

  // Data
  if (!data) return null
  const { features, heading, finePrint } = data
  if (!features || !heading || !finePrint) return null

  return (
    <div
      className="FINDEX-DIFFERENCE-BLOCK relative min-h-[600px] w-full overflow-hidden"
      id={sectionId}
    >
      <BlurryLightsDaytime />
      <div className="FINDEX-DIFFERENCE-BLOCK-INNER max-w-container p-gutter relative z-10 mx-auto py-20">
        <h2 className="mb-6 md:mb-12" ref={sectionRef}>
          {heading}
        </h2>
        {/** Desktop version */}
        <div className="FEATURES-GRID hidden grid-cols-2 gap-5 sm:grid md:gap-6 lg:grid-cols-3 xl:gap-10">
          {features?.map((feature, featureNumber) => {
            return (
              <FindexFeatureCard
                key={`feature-${featureNumber}-${feature.icon?.alt}`}
                {...feature}
              />
            )
          })}
        </div>
        <p className="FINE-PRINT para-sm text-subtle mt-14 hidden italic md:block">{finePrint}</p>

        {/** Mobile version */}
        <div className="CAROUSEL-CONTAINER w-screen sm:hidden">
          <Carousel className="CAROUSEL" opts={{ align: 'start' }}>
            <CarouselPrevious className="border-subtle text-subtle top-[93%] left-0 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-23 md:left-[unset]" />
            <CarouselNext className="border-subtle text-subtle top-[93%] left-13 z-10 size-10 cursor-pointer bg-white/0 hover:scale-105 hover:bg-white/0 md:-top-21 md:right-10 md:left-[unset]" />

            <CarouselContent className="CAROUSEL-CONTENT ml-0 gap-6 pb-26 md:ml-12 md:gap-10 md:overflow-visible md:pb-2">
              {features?.map((feature, featureNumber) => {
                return (
                  <FindexFeatureCard
                    key={`feature-${featureNumber}-${feature.icon?.alt}`}
                    {...feature}
                    isLastItem={featureNumber === features.length - 1}
                  />
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </div>
  )
}
